/**
 * Debug Logger Utility
 * Centralized logging that only works in development mode
 * Prevents sensitive data from being logged in production
 * Integrates with Sentry for error tracking in production
 */

const DEBUG = process.env.NODE_ENV === 'development';

// Lazy load Sentry to avoid issues if not configured
let Sentry = null;
try {
  Sentry = require('@sentry/react');
} catch (e) {
  // Sentry not available or not configured
}

/**
 * Log debug messages (only in development)
 * @param {string} component - Component name
 * @param {string} message - Log message
 * @param {*} data - Optional data to log
 */
export const debugLog = (component, message, data = null) => {
  if (DEBUG) {
    if (data !== null) {
      console.log(`[${component}] ${message}`, data);
    } else {
      console.log(`[${component}] ${message}`);
    }
  }
};

/**
 * Log warnings (always logged)
 * @param {string} component - Component name
 * @param {string} message - Warning message
 * @param {*} data - Optional data to log
 */
export const warnLog = (component, message, data = null) => {
  if (data !== null) {
    console.warn(`[${component}] ${message}`, data);
  } else {
    console.warn(`[${component}] ${message}`);
  }
};

/**
 * Log errors (always logged)
 * Also sends to Sentry in production
 * @param {string} component - Component name
 * @param {string} message - Error message
 * @param {Error} error - Error object
 */
export const errorLog = (component, message, error = null) => {
  if (error) {
    console.error(`[${component}] ${message}`, error);
    
    // Send to Sentry if available and in production
    if (Sentry && process.env.NODE_ENV === 'production') {
      try {
        Sentry.captureException(error, {
          tags: {
            component,
            source: 'errorLog',
          },
          extra: {
            message,
            component,
          },
        });
      } catch (sentryError) {
        // Don't let Sentry errors break the app
        console.error('[Sentry] Failed to log error:', sentryError);
      }
    }
  } else {
    console.error(`[${component}] ${message}`);
    
    // Send message to Sentry if available and in production
    if (Sentry && process.env.NODE_ENV === 'production') {
      try {
        Sentry.captureMessage(`[${component}] ${message}`, {
          level: 'error',
          tags: {
            component,
            source: 'errorLog',
          },
        });
      } catch (sentryError) {
        // Don't let Sentry errors break the app
        console.error('[Sentry] Failed to log message:', sentryError);
      }
    }
  }
};

/**
 * Log info messages (only in development)
 * @param {string} component - Component name
 * @param {string} message - Info message
 * @param {*} data - Optional data to log
 */
export const infoLog = (component, message, data = null) => {
  if (DEBUG) {
    if (data !== null) {
      console.info(`[${component}] ${message}`, data);
    } else {
      console.info(`[${component}] ${message}`);
    }
  }
};

/**
 * Sanitize sensitive data before logging
 * @param {*} data - Data to sanitize
 * @returns {*} Sanitized data
 */
export const sanitizeForLogging = (data) => {
  if (!data) return data;
  
  if (typeof data === 'string') {
    // Truncate long strings (like transcripts)
    if (data.length > 500) {
      return data.substring(0, 500) + '... (truncated)';
    }
    return data;
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeForLogging(item));
  }
  
  if (typeof data === 'object') {
    const sanitized = { ...data };
    // Remove sensitive fields
    const sensitiveFields = ['transcript', 'text', 'password', 'token', 'apiKey'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });
    return sanitized;
  }
  
  return data;
};

/**
 * Safe debug log that sanitizes sensitive data
 * @param {string} component - Component name
 * @param {string} message - Log message
 * @param {*} data - Data to log (will be sanitized)
 */
export const safeDebugLog = (component, message, data = null) => {
  if (DEBUG) {
    const sanitized = sanitizeForLogging(data);
    debugLog(component, message, sanitized);
  }
};

